/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/ban-types */
import React, { useState, Fragment, ReactNode, useEffect } from 'react';

import {
  isClient,
  ONE_SECOND_MS,
} from '../../constants';

// import { SharePanel } from '../SharePanel/SharePanel';
import { StoryProgress } from './StoryProgress';
// import { StorySelector } from '../StorySelector/StorySelector';

import {
  AlecTitle,
  ChoicesContainer,
  ConversationContainer,
  ConversationLoading,
  FeedContainer,
  ResultDescription,
  ResultTitle,
  StyledStoryChoice,
  StyledStoryChoiceOption,
  StyledStoryDialogue,
  StyledStoryImageContainer,
  StyledStoryNarration,
  StyledStoryResultFeedback,
  StyledStoryResultResponse,
  StyledStorySelectorsContainer,
  StyledStorySpeakerImage,
  StyledStoryView,
  StyledStoryViewFeed,
  StyledStoryViewFeedContainer,
  StyledStoryViewInner,
  StyledContinueButtonContainer,
  Speaker,
  TimeLog,
} from './StoryView.styled';
// import { StoryViewRemember } from './StoryViewRemember/StoryViewRemember';
import { IStory, IStoryChoice,
    IStoryDialogue,
    IStoryNarration,
    IStoryOption,
    IStoryRemember,
    STORY_STEP_TYPES,
} from './IStories';
import LazyImage from './LazyImage';
import { JavascriptWarning } from './JavascriptWarning';
import { t } from 'i18next';
import { Button } from 'components/Button/Button';
import { useNavigate } from 'react-router-dom';
import { getImageFilePath, pageScroll } from 'utils/helper';

const STORY_VIEW_ID = 'storyView';
const FEED_CONTAINER_ID = 'feedContainer';
const CHOICE_CONTAINER_ID = 'choiceContainer';

export interface StoryViewProps {
  stories: IStory[];
  story: IStory;
  className?: string;
  history?: any;
  onStoryComplete?: Function;
}

export interface StoryViewState {
  storyStepIdx: number; // The index of the story step that is currently shown
  storyStartedAt?: Date;
  shownComponents: ReactNode[];
  incorrectSelectedOptions: IStoryOption[];
  speaker1Image?: string;
  speaker2Image?: string;
  hasEnded: boolean;
  stepIndex: number; //The index of the choice that the user has selected
  isDone: boolean;
  isCorrectChoiceSelected: boolean;
  readyToSelect: boolean;
  showLearnTheSkills: boolean;
  areAllAnswersCorrect: boolean;

  //If not empty, will display the continue button and perform this function on click.
  //Currently only used after showing a 'REMEMBER' section upon choice selection
  continueButtonFunc?: () => void;
}

const StoryViewFC: React.FC<StoryViewProps > = ({ story, className, stories, onStoryComplete }) => {
  let keyCount: number; //Used to uniquely identify components when mapping them out
  // let stepShowTimeout: any; //Any settimeouts for showing components need to use this so it can be cleared/overridden
  // let savedScrollTop: number; // Used to save the position of scroll before the components change, then instantly restore it and scroll down smoothly

  const [storyStepIdx, setStoryStepIdx] = useState(-1);
  const [storyStartedAt, setStoryStartedAt] = useState<Date | undefined>(undefined);
  const [shownComponents, setShownComponents] = useState<React.ReactNode[]>([]);
  const [incorrectSelectedOptions, setIncorrectSelectedOptions] = useState<IStoryOption[]>([]);
  const [hasEnded, setHasEnded] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [isDone, setIsDone] = useState(false);
  const [readyToSelect, setReadyToSelect] = useState(false);
  const [isCorrectChoiceSelected, setIsCorrectChoiceSelected] = useState(false);
  const [showLearnTheSkills, setShowLearnTheSkills] = useState(false);
  const [areAllAnswersCorrect, setAreAllAnswersCorrect] = useState(true);
  const [continueButtonFunc, setContinueButtonFunc] = useState<Function | undefined>(undefined);
  const [speaker1Image, setSpeaker1Image] = useState<string | undefined>(undefined);
  const [speaker2Image, setSpeaker2Image] = useState<string | undefined>(undefined);

  const navigate = useNavigate();

  keyCount = 0;
  // stepShowTimeout = null;
  // savedScrollTop = 0;

  const addShownComponents = (components: ReactNode[], callbackFn?: () => void) => {
    //Save the scroll position to 'lock' it in place
    pageScroll.savePosition(false, FEED_CONTAINER_ID);

    setShownComponents([...shownComponents, ...components]);

    if (callbackFn) callbackFn();
  }

  const addShownComponent = (component: ReactNode, callbackFn?: () => void) => {
    addShownComponents([component], callbackFn);
  }

  const showActiveStoryStep = () => {
    if (storyStepIdx === -1) {
      return;
    }

    const storyStep = story.storySteps[storyStepIdx];
    if (!storyStep) throw Error('Story step not found');

    const indexOfStep = story.storySteps.indexOf(storyStep);
    const nextStoryStep = story.storySteps[indexOfStep + 1];

    //As the switch below considers each step type, these three variables may
    // be assigned values that are then used/set at the end of this function
    let $continueButtonFunc;
    let storyStepComponents: ReactNode[] = [];
    let nextStepAfterDelay: number | null = null; //If null/empty, the next step will not show automatically

    if (storyStep._type === STORY_STEP_TYPES.CHOICE) {
      setIsCorrectChoiceSelected(false);
      setReadyToSelect(true);
    } else {
      switch (storyStep._type) {
        case STORY_STEP_TYPES.DIALOGUE:
          const dialogueStoryStep = storyStep as IStoryDialogue;
          const nextStepIsDialogue =
            nextStoryStep && nextStoryStep._type === STORY_STEP_TYPES.DIALOGUE;

          //LH: Tried doing this in CSS but it was a nightmare (nub shows when last speech bubble for a particular speaker in a row)
          const showNub =
            !nextStoryStep ||
            !nextStepIsDialogue ||
            (nextStoryStep as IStoryDialogue).speakerId !== dialogueStoryStep.speakerId;

          storyStepComponents = [
            <>
              <StyledStoryDialogue
                key={keyCount++}
                showNub={showNub}
                className={`speaker-${dialogueStoryStep.speakerId}`}
                speakerId={dialogueStoryStep.speakerId}
              >
                {dialogueStoryStep.text}
              </StyledStoryDialogue>
              {!nextStepIsDialogue && (
                <TimeLog>
                  {story.characterName || 'Matt'} -{' '}
                  {new Date().toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </TimeLog>
              )}
            </>,
          ];
          nextStepAfterDelay = nextStepIsDialogue ? ONE_SECOND_MS : 2 * ONE_SECOND_MS;
          break;

        case STORY_STEP_TYPES.NARRATION:
          const narrationStoryStep = storyStep as IStoryNarration;

          storyStepComponents = [
            <StyledStoryNarration key={keyCount++} level='h6'>
              {narrationStoryStep.text}
            </StyledStoryNarration>,
          ];
          nextStepAfterDelay = 1.5 * ONE_SECOND_MS;
          break;

        case STORY_STEP_TYPES.REMEMBER:
          const rememberStoryStep = storyStep as IStoryRemember;
          console.log('rememberStoryStep', rememberStoryStep);
          storyStepComponents = [
            // <StoryViewRemember key={keyCount++} content={rememberStoryStep.content} />,
            <div>{rememberStoryStep.content} </div>,
          ];
          $continueButtonFunc = () => {
            removeLastShownComponent();
            showNextStoryStep(ONE_SECOND_MS);
            setIsDone(true);
          };

        //   DataLayer.pushConversationPause('remember');

        //   DataLayer.pushConversationView(story);
          break;

        case STORY_STEP_TYPES.HOMEWORK:
          if (storyStartedAt) {
            // DataLayer.pushOldConversationComplete(new Date().getTime() - storyStartedAt.getTime());
            // DataLayer.pushConversationComplete(story, areAllAnswersCorrect);
          }
          if (onStoryComplete) {
            onStoryComplete();
          } else {
            navigate('/dashboard');
            // router.push({
            //   pathname: getRoute.conversationResult(),
            //   query: {
            //     title: story.name,
            //     currentStep: stepIndex,
            //     totalSteps: story.storySteps.filter(
            //       ({ _type }) => _type === STORY_STEP_TYPES.CHOICE,
            //     ).length,
            //     isDone: isDone,
            //   },
            // });
          }
          break;
        default:
          throw Error('Invalid Story type');
      }

      addShownComponents(storyStepComponents);
      if ($continueButtonFunc) {
        setContinueButtonFunc($continueButtonFunc);
      }
    }

    if (nextStepAfterDelay) showNextStoryStep(nextStepAfterDelay);
  }

  useEffect(() => {
    pageScroll.toBottomOfId(FEED_CONTAINER_ID);
    setStoryStepIdx(0);
    setStoryStartedAt(new Date());
    setSpeaker1Image( story.speaker1Image);
    setSpeaker2Image( story.speaker2Image);
    // DataLayer.pushConversationStart(story);
    // DataLayer.pushStoryStart(story);
    setTimeout(() => showActiveStoryStep(), 1.5 * ONE_SECOND_MS);

  }, [story]);


  const endStory = () => {
    // addShownComponent(
    //   <StyledSharePanelContainer>
    //     <SharePanel />
    //   </StyledSharePanelContainer>,
    // );

    addShownComponent(<div> share panel</div>);

    if (storyStartedAt) {
    //   DataLayer.pushOldConversationComplete(new Date().getTime() - storyStartedAt.getTime());
    //   DataLayer.pushConversationComplete(story, areAllAnswersCorrect);
    }
    setTimeout(() => {
      setStoryStepIdx(0);
      setStoryStartedAt(undefined);
      setHasEnded(true);
      
    }, 2 * ONE_SECOND_MS);
  }

  const showNextStoryStep = (delay = 0) => {
    const nextStoryStepIdx = storyStepIdx + 1;
    const nextStoryStep = story.storySteps[nextStoryStepIdx];

    setTimeout(() => {
      if (nextStoryStep) {
        setStoryStepIdx(nextStoryStepIdx);
      } else {
        //Story steps are all shown, end the story
        endStory();
      }
    }, delay);
  }

  useEffect(() => {
    // Trigger next step after storyStepIdx has been updated
    if (storyStepIdx === -1) {
      return;
    }
    showActiveStoryStep();
  }, [storyStepIdx]);

  useEffect(() => {
    scrollToBottom();
  }, [isCorrectChoiceSelected, readyToSelect]);

  useEffect(() => {
    setTimeout(() => scrollToBottom(), 0.5 * ONE_SECOND_MS);
  }, [showLearnTheSkills])

  useEffect(() => {
    pageScroll.toBottomOfId(FEED_CONTAINER_ID);
  }, [hasEnded, shownComponents]);



  const showQuestion = (index: number) => {
    const questions =
      story.storySteps.filter(({ _type }) => _type === STORY_STEP_TYPES.CHOICE) || [];
    const choiceStoryStep = questions[
      index >= questions.length ? questions.length - 1 : index
    ] as IStoryChoice;

    const choiceComponent = (
      <StyledStoryChoice key={index}>
        {choiceStoryStep.choiceOptions.map((option, idx) => {
          return (
            <StyledStoryChoiceOption
              key={idx}
              isCorrect={isCorrectChoiceSelected && option.correct}
              onClick={() => selectChoiceOption(choiceStoryStep, option)}
              disabled={!readyToSelect || incorrectSelectedOptions.includes(option)}
            >
              {option.text}
            </StyledStoryChoiceOption>
          );
        })}
      </StyledStoryChoice>
    );
    return choiceComponent;
  }



  const removeLastShownComponent = (callbackFn?: () => void) => {
    shownComponents.pop();

    setShownComponents([...shownComponents]);
    if (callbackFn) callbackFn();
  }

  const selectChoiceOption = ({ skill }: IStoryChoice, option: IStoryOption) => {
    const dialogue = story.storySteps[stepIndex] as IStoryDialogue;
    const questionNumber = stepIndex + 1;

    // DataLayer.pushConversationFeedbackShown(story, option, dialogue, questionNumber);
    // DataLayer.pushConversationAnswer(story, option, dialogue, questionNumber); 

    if (!option.correct) {
      setAreAllAnswersCorrect(false);
    }
   
    setIsCorrectChoiceSelected(option.correct);
    setReadyToSelect(false);

    const onContinueClick = () => {
      pageScroll.toBottomOfId(FEED_CONTAINER_ID);

      //On the continue button click, we remove it- and either proceed to next step (if correct) or re-show the choice step (if incorrect)
      if (option.correct) {
        setIncorrectSelectedOptions([]);
        setStepIndex(stepIndex + 1);
        setIsCorrectChoiceSelected(false);
        //Callback: progress to the next story step
        showNextStoryStep(ONE_SECOND_MS);
      } else {
        //Callback: show the SAME choice set again
        setIncorrectSelectedOptions([...incorrectSelectedOptions, option]);
        showActiveStoryStep();
      }
    };

    addShownComponents([
      <StyledStoryResultResponse
        key={keyCount++}
        showNub={true}
        speakerId={1}
        correct={option.correct}
      >
        {option.text}
      </StyledStoryResultResponse>,
      <TimeLog key={keyCount++} rightSide={true}>
        You -{' '}
        {new Date().toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        })}
      </TimeLog>,
      <StyledStoryResultFeedback key={keyCount++} isCorrect={option.correct}>
        <ResultTitle>
          {option.correct ? (
            <>
              Nice Work
              {skill && ` - ${skill}`}
              <LazyImage src='/images/icons/chatmate/reviewed_icon.svg' alt='Reviewed' />
            </>
          ) : (
            <>
              Nice try, but...
              <LazyImage src='/images/icons/chatmate/warn_exclaim.svg' alt='Warning' />
            </>
          )}
        </ResultTitle>
        <ResultDescription
          key={keyCount++}
          dangerouslySetInnerHTML={{ __html: option.feedbackContent }}
        />
      </StyledStoryResultFeedback>,
    ]);

    // this.setState({
    //   continueButtonFunc: onContinueClick,
    // });
    setContinueButtonFunc(onContinueClick);
  }

  const scrollToBottom = () => {
    if (isClient) {
      pageScroll.toBottomOfId();
    }
  }



  // TODO: I dont think its been using. double check then remove this later.
  // const showLearnTheSkillsFunc = () => {

  //   // this.setState({ showLearnTheSkills: true }, () =>
  //   //   setTimeout(() => this.scrollToBottom(), 0.5 * ONE_SECOND_MS),
  //   // );
  //   setShowLearnTheSkills(true);
  //   setTimeout(() => this.scrollToBottom(), 0.5 * ONE_SECOND_MS)
  //   DataLayer.push({
  //     event: DATA_LAYER_EVENTS.BUTTON_CLICK,
  //     label: `Learn the skills - ${story.name}`.trim(),
  //   });
  //   DataLayer.pushClickCTA({
  //     location: `${DOMAIN}${this.props.router.asPath}`,
  //     link_text: `Learn the skills - ${story.name}`.trim(),
  //   });
  // }

  const handleContinueButtonClick = () => {
    if (continueButtonFunc) continueButtonFunc();
    // this.setState({
    //   continueButtonFunc: undefined,
    // });
    setContinueButtonFunc(undefined);
  }

  const countQuestions = story.storySteps.filter(
    ({ _type }) => _type === STORY_STEP_TYPES.CHOICE,
  ).length;

  const speaker1ImageEl = speaker1Image ? (
    <StyledStorySpeakerImage
      speakerId={1}
      src={getImageFilePath(speaker1Image)}
      alt='Speaker 1'
    />
  ) : null;
  const speaker2ImageEl = speaker2Image ? (
    <StyledStorySpeakerImage
      speakerId={2}
      src={getImageFilePath(speaker2Image)}
      alt='Speaker 2'
    />
  ) : null;

  return (
    <StyledStoryView className={className}>
      <StyledStoryImageContainer speakerId={1}>{speaker1ImageEl}</StyledStoryImageContainer>
      <StyledStoryImageContainer speakerId={2}>{speaker2ImageEl}</StyledStoryImageContainer>
     
      <JavascriptWarning />
      <StoryProgress
        title={story.name}
        currentStep={stepIndex}
        totalSteps={countQuestions}
        showArrow={false}
        useHref={false}
        isDone={isDone}
      ></StoryProgress>
      <StyledStoryViewInner id={STORY_VIEW_ID}>
        <ConversationContainer isActive={readyToSelect}>
          <StyledStoryViewFeedContainer>
            <ConversationLoading src='/images/icons/chatmate/conversation_loading.gif' />
            <Speaker isRight={false}>
              <LazyImage src={getImageFilePath(story.speaker2Thumbnail ?? '')} alt='Left' />
            </Speaker>
            <FeedContainer id={FEED_CONTAINER_ID}>
              <StyledStoryViewFeed>
                {shownComponents.map((component, idx) => (
                  <Fragment key={idx}>{component}</Fragment>
                ))}
                {hasEnded && (
                  <StyledStorySelectorsContainer>
                    {/* {stories.map((eachStory, idx) => (
                      <StorySelector key={idx} story={eachStory} />
                    ))} */}
                    <div>Story Selector</div>
                  </StyledStorySelectorsContainer>
                )}
              </StyledStoryViewFeed>
            </FeedContainer>
            <Speaker isRight={true}>
              <LazyImage src={getImageFilePath(story.speaker1Thumbnail ?? '')} alt='Right' />
            </Speaker>
            {!readyToSelect && (
              <StyledContinueButtonContainer isVisible={!!continueButtonFunc}>
                <Button styled={{ width: '100%' }}  onClick={() => handleContinueButtonClick()} label='Continue' />
              </StyledContinueButtonContainer>
            )}
          </StyledStoryViewFeedContainer>
        </ConversationContainer>
        <ChoicesContainer isActive={readyToSelect} id={CHOICE_CONTAINER_ID}>
            <>
              <AlecTitle isActive={readyToSelect}>
                Use the
                <a href='#' onClick={() => setShowLearnTheSkills(true)}>
                    ALEC skills
                </a>
                <span> to respond:</span>
              </AlecTitle>
              {showQuestion(stepIndex)}
            </>
        </ChoicesContainer>
      </StyledStoryViewInner>
    </StyledStoryView>
  );
}

export default StoryViewFC;
